import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import { ENABLE_STUDENT_ALERTS } from "Constants/permissionConstants";

import {
  HeartLikeFilled,
  UserRaisedArmsFilled,
  UserArrowsFilled,
  HammerFilled,
  UserShieldFilled,
  AssessmentFilled,
  MinusCircleFilled,
  BallFilled,
  BoltFilled,
  ChatBubbleFilled,
  StarShieldFilled,
  WarningCircleFilled,
  WheelChairFilled,
  HeadGearFilled,
  PillFilled,
  EnvelopeFilled,
  FlagFilled,
  SuitcasePlusFilled,
  AboriginalFlagColored,
  TorresIslandFlagColored,
} from "@toddle-edu/ds-icons";

import {
  getOrganizationId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { getOrganizationGlobalConstantsFromCache } from "modules/CommonGraphqlHelpers";

export const MODAL_MODES = {
  ADD: "ADD",
  EDIT: "EDIT",
};

export const ALERT_TYPE_ICON_CONSTANT = "STUDENT_ALERT_TYPE_ICONS";

// Fetch dynamic alert type icons from global constants
const getAlertTypeIconsFromCache = () => {
  const globalConstantsData = getOrganizationGlobalConstantsFromCache({
    organizationId: getOrganizationId(),
    filter: {
      names: [ALERT_TYPE_ICON_CONSTANT],
    },
  });

  const icons = _.get(
    globalConstantsData,
    "node.globalConstants[0].value",
    "[]"
  );

  return JSON.parse(icons);
};

export const getCurriculumTagColor = ({ curriculumProgramType }) => {
  let tagColor = "pink";

  switch (curriculumProgramType) {
    case "IB_MYP":
      tagColor = "violet";
      break;
    case "IB_DP":
      tagColor = "yellow";
      break;
    case "UBD":
      tagColor = "blue";
      break;
  }

  return tagColor;
};

export const getIsStudentAlertsEnabled = () => {
  return ACLStore.can(ENABLE_STUDENT_ALERTS);
};

export const getIsSchoolUIEnabledInAlerts = () => {
  return ACLStore.can("FeatureFlag:EnableSchoolUIInAlerts");
};

export const alertTypeIconPalette = [
  {
    name: "MedicalAlertColored",
    icon: HeartLikeFilled,
    iconProps: {
      style: { color: "var(--icon-semantic-error)" },
    },
    dataGridIconName: "heartLikeAlert",
  },
  {
    name: "SpecialNeedsColored",
    icon: UserRaisedArmsFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-violet)" },
    },
    dataGridIconName: "specialNeedsAlert",
  },
  {
    name: "BehaviorAlertColored",
    icon: UserArrowsFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-violet)" },
    },
    dataGridIconName: "userArrowsAlert",
  },
  {
    name: "CustodyAlertColored",
    icon: UserShieldFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-orange)" },
    },
    dataGridIconName: "userShieldAlert",
  },
  {
    name: "StudentPlanAlertColored",
    icon: AssessmentFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-teal)" },
    },
    dataGridIconName: "assessmentAlert",
  },
  {
    name: "CourtOrderColored",
    icon: HammerFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-orange)" },
    },
    dataGridIconName: "hammerAlert",
  },
  {
    name: "MinusCircleFilled",
    icon: MinusCircleFilled,
    iconProps: {
      style: { color: "var(--icon-semantic-error)" },
    },
    dataGridIconName: "minusCircleAlert",
  },
  {
    name: "BallFilled",
    icon: BallFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-green)" },
    },
    dataGridIconName: "ballAlert",
  },
  {
    name: "BoltFilled",
    icon: BoltFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-pink)" },
    },
    dataGridIconName: "boltAlert",
  },
  {
    name: "ChatBubbleFilled",
    icon: ChatBubbleFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-blue)" },
    },
    dataGridIconName: "chatBubbleAlert",
  },
  {
    name: "StarShieldFilled",
    icon: StarShieldFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-purple)" },
    },
    dataGridIconName: "policyAlert",
  },
  {
    name: "WarningCircleFilled",
    icon: WarningCircleFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-yellow)" },
    },
    dataGridIconName: "warningCircleAlert",
  },
  {
    name: "WheelChairFilled",
    icon: WheelChairFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-pink)" },
    },
    dataGridIconName: "wheelChairAlert",
  },
  {
    name: "HeadGearFilled",
    icon: HeadGearFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-teal)" },
    },
    dataGridIconName: "headGearAlert",
  },
  {
    name: "PillFilled",
    icon: PillFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-blue)" },
    },
    dataGridIconName: "pillAlert",
  },
  {
    name: "SuitcasePlusFilled",
    icon: SuitcasePlusFilled,
    iconProps: {
      style: { color: "var(--icon-semantic-error)" },
    },
    dataGridIconName: "injuryAlert",
  },
  {
    name: "EnvelopeFilled",
    icon: EnvelopeFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-yellow)" },
    },
    dataGridIconName: "envelopeAlert",
  },
  {
    name: "FlagFilled",
    icon: FlagFilled,
    iconProps: {
      style: { color: "var(--decorative-foreground-subtle-pink)" },
    },
    dataGridIconName: "flagAlert",
  },
  {
    name: "AboriginalFlagColored",
    icon: AboriginalFlagColored,
    dataGridIconName: "aboriginalFlagAlert",
  },
  {
    name: "TorresIslandFlagColored",
    icon: TorresIslandFlagColored,
    dataGridIconName: "torresIslandFlagAlert",
  },
];

export const getAlertTypeIconPalette = () => {
  const customIcons = getAlertTypeIconsFromCache();

  return [...alertTypeIconPalette, ...customIcons];
};

export const getStudentAlertTypeIcon = ({ iconMetaData }) => {
  const name = _.get(iconMetaData, "name");
  const iconConfig = _.find(alertTypeIconPalette, { name });

  let Icon = iconConfig?.icon;
  let iconProps = iconConfig?.iconProps || {};

  if (!Icon) {
    const DSIcons = require("@toddle-edu/ds-icons");
    Icon = _.get(DSIcons, name);
    iconProps = _.get(iconMetaData, "iconProps", {});
  }

  const CustomizedIcon = props => <Icon {...iconProps} {...props} />;
  CustomizedIcon.displayName = name;

  return CustomizedIcon;
};

export const getDataGridAlertTypesIcon = ({
  alertTypes = [],
  isGradebookDatagrid = true,
}) => {
  if (!getIsStudentAlertsEnabled()) {
    return [];
  }

  const DATAGRID_FALLBACK_ICON = "flagAlert";

  const iconNames = _.reduce(
    alertTypes,
    (acc, alertType) => {
      const iconName = _.get(alertType, "icon.name");

      const iconConfig = _.find(alertTypeIconPalette, { name: iconName });

      if (!iconConfig) {
        const dataGridIconName = _.get(
          alertType,
          "icon.dataGridIconName",
          DATAGRID_FALLBACK_ICON
        );

        return [...acc, dataGridIconName];
      }

      return [...acc, iconConfig.dataGridIconName];
    },
    []
  );

  // Due to difference in icon name nomenclature between Gradebook and Datagrid,
  // we need to convert the icon names to PascalCase for Datagrid, while keeping them in camelCase for Gradebook
  if (!isGradebookDatagrid) {
    return _.map(iconNames, _.upperFirst);
  }

  return iconNames;
};

export const getStudentAlertsNomenclature = ({ t } = {}) => {
  // TODO: Replace hardcoded values with query cache once BE support is added
  const globalConstants = [
    {
      name: "ALERTS_MODULE_NOMENCLATURE_ALERT",
      value: t ? t("studentAlert:flag") : "Flag",
    },
    {
      name: "ALERTS_MODULE_NOMENCLATURE_MODULE",
      value: t ? t("studentAlert:student_flag") : "Student flag",
    },
  ];

  const NOMENCLATURE_NAME_MAP = {
    ALERTS_MODULE_NOMENCLATURE_ALERT: "alert",
    ALERTS_MODULE_NOMENCLATURE_MODULE: "module",
  };

  const NOMENCLATURE_FALLBACK_MAP = {
    [NOMENCLATURE_NAME_MAP.ALERTS_MODULE_NOMENCLATURE_ALERT]: "Alert",
    [NOMENCLATURE_NAME_MAP.ALERTS_MODULE_NOMENCLATURE_MODULE]: "Student alerts",
  };

  const nomenclatureConstants = _.reduce(
    globalConstants,
    (acc, item) => {
      const { name, value } = item;
      const customName = NOMENCLATURE_NAME_MAP[name];

      if (!customName) {
        return acc;
      }

      return {
        ...acc,
        [customName]: value,
      };
    },
    {}
  );

  _.forEach(NOMENCLATURE_FALLBACK_MAP, (fallbackValue, nomenclatureKey) => {
    if (_.isEmpty(nomenclatureConstants[nomenclatureKey])) {
      nomenclatureConstants[nomenclatureKey] = fallbackValue;
    }
  });

  return nomenclatureConstants;
};

export const getStudentAlertTypeVariables = ({ state }) => {
  const organizationId = getOrganizationId(state);
  const academicYearId = getSelectedAcademicYearId(state);

  return {
    id: organizationId,
    countFilter: { academicYearId },
  };
};
